import {
    Box, Chip, Grid, Skeleton, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TableSortLabel, Tooltip, IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CampaignIcon from '@mui/icons-material/Campaign';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import { iconSize } from 'assets/theme/iconSize';
import { setLoading, useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { formatPhone } from '@functions/format-phone';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '../../kanban-board/crm-theme';
import { leadChannel, readableChannelColor } from '../../lead/lead-channel';
import { PersonResponse, PersonSource } from '../services/responses/person.response';
import { SOURCE_DETAIL_KEY } from '../../kanban-board/formatters/lead-source-label';
import { ELeadSourceDetail } from '../../kanban-board/services/responses/lead.response';
import { PagedResponse } from 'types/paged-response';
import { PersonService } from '../services/person.service';

interface Props {
    data: PagedResponse<PersonResponse>;
    editAction: (id: string) => void;
    refresh: boolean;
    setRefresh: (v: boolean) => void;
    orderBy: string;
    orderDirection: 'asc' | 'desc';
    onOrder: (field: string) => void;
    /// Com busca aplicada, lista vazia é "não achou", não "não tem ninguém".
    searching: boolean;
}


/// Origens que chegam por canal; o anúncio tem rótulo traduzido próprio e o manual é o que sobra.
function SourceChip({ person, manualLabel, adLabel, importLabel, rdStationLabel,
    detailLabel }: {
    person: PersonResponse;
    manualLabel: string;
    adLabel: string;
    importLabel: string;
    rdStationLabel: string;
    /// "Formulário do Facebook", "Direct do Instagram"...; nulo quando a pessoa não tem detalhe.
    detailLabel: string | null;
}) {
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    // A cor fica na borda e no ícone; o texto na cor do chip ficava apagado.
    const labelSx = { '& .MuiChip-label': { color: cc.onTint } };

    if (person.source === PersonSource.Import) {
        return (
            <Chip
                icon={<UploadFileIcon sx={{ fontSize: 13 }} />}
                label={importLabel}
                size="small"
                color="info"
                variant="outlined"
                sx={labelSx}
            />
        );
    }

    if (person.source === PersonSource.RdStation) {
        return (
            <Chip
                icon={<HubOutlinedIcon sx={{ fontSize: 13 }} />}
                label={rdStationLabel}
                size="small"
                color="primary"
                variant="outlined"
                sx={labelSx}
            />
        );
    }

    if (person.source === PersonSource.AdClick) {
        return (
            <Chip
                icon={<CampaignIcon sx={{ fontSize: 13 }} />}
                label={[adLabel, detailLabel].filter(Boolean).join(' · ')}
                size="small"
                color="warning"
                variant="outlined"
                sx={labelSx}
            />
        );
    }

    // Mesma cor e ícone do canal no kanban e no Multicanal: as cores do tema não são as das marcas.
    const channel = leadChannel(person.source);

    if (channel) {
        const color = readableChannelColor(channel.color, cc.cardBg, cc.textPrimary);

        return (
            <Chip
                icon={<channel.Icon sx={{ fontSize: 13, color: `${color} !important` }} />}
                label={channel.label}
                size="small"
                variant="outlined"
                sx={{ ...labelSx, borderColor: color }}
            />
        );
    }

    return <Chip label={manualLabel} size="small" variant="outlined" sx={labelSx} />;
}

export function PeopleTable({
    data, editAction, refresh, setRefresh, orderBy, orderDirection, onOrder, searching,
}: Props) {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert, alertDelete } = useAlert();

    const records: PersonResponse[] = data?.data?.records ?? [];

    const sourceChip = (person: PersonResponse) => (
        <SourceChip
            person={person}
            manualLabel={t('people.manual')}
            adLabel={t('people.anuncio')}
            importLabel={t('people.importacao')}
            rdStationLabel={t('crmMultichannel.origemRdStation')}
            detailLabel={person.sourceDetail ? t(SOURCE_DETAIL_KEY[person.sourceDetail as number as ELeadSourceDetail]) : null}
        />
    );

    function doDelete(id: string) {
        setLoading(dispatch, true);
        new PersonService()
            .deleteById(id)
            .then(() => {
                showAlert(t('people.excluirSucesso'), 'success', 2500);
                setRefresh(!refresh);
            })
            .catch((err) => showAlert(apiErrorMessage(err), 'error', 3000))
            .finally(() => setLoading(dispatch, false));
    }

    return (
        <TableContainer
            sx={{
                marginTop: '15px',
                maxHeight: '60vh',
                overflowY: 'auto',
            }}
        >
            <Table size="medium">
                <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'background.card' }}>
                    <TableRow hover>
                        <TableCell width="24%" sortDirection={orderBy === 'Name' ? orderDirection : false}>
                            <TableSortLabel
                                active={orderBy === 'Name'}
                                direction={orderBy === 'Name' ? orderDirection : 'asc'}
                                onClick={() => onOrder('Name')}
                            >
                                {t('general.nome')}
                            </TableSortLabel>
                        </TableCell>
                        <TableCell width="16%">{t('people.telefone')}</TableCell>
                        <TableCell width="18%">{t('people.empresa')}</TableCell>
                        <TableCell width="18%">{t('people.email')}</TableCell>
                        <TableCell width="12%">{t('people.origem')}</TableCell>
                        <TableCell width="12%" align="center">{t('general.acoes')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {!data ? (
                        Array.from({ length: 5 }).map((_, index) => (
                            <TableRow key={index}>
                                {Array.from({ length: 6 }).map((__, cell) => (
                                    <TableCell key={cell}><Skeleton height={28} /></TableCell>
                                ))}
                            </TableRow>
                        ))
                    ) : records.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={6} align="center">{t(searching ? 'people.nenhumResultado' : 'people.nenhumaPessoa')}</TableCell>
                        </TableRow>
                    ) : (
                        records.map((person) => (
                            <TableRow key={person.id} hover>
                                <TableCell>{person.name}</TableCell>
                                <TableCell>{person.phone ? formatPhone(person.phone) : '-'}</TableCell>
                                <TableCell>{person.companyName || '-'}</TableCell>
                                <TableCell>{person.email || '-'}</TableCell>
                                <TableCell>{sourceChip(person)}</TableCell>
                                <TableCell align="center">
                                    <Grid container justifyContent="center">
                                        <Tooltip title={t('general.editar')}>
                                            <IconButton size="small" sx={{ p: 0.25 }} onClick={() => editAction(person.id)}>
                                                <Box component={EditIcon} sx={iconSize} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('general.excluir')}>
                                            <IconButton size="small" sx={{ p: 0.25 }} onClick={() => alertDelete(person.id, doDelete, person.name)}>
                                                <Box component={DeleteIcon} sx={{ ...iconSize, color: 'error.main' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
